import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction";
import { hasInstantBookCriteriaUnmet } from "@src/appV2/OpenShifts/ShiftAction/hasInstantBookCriteriaUnmet";
import { hasUnmetBookabilityCriteria } from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import { useGetBlockingRequirementsForShift } from "@src/appV2/redesign/Documents/api/wrappers/useGetBlockingRequirementsForShift";
import { getDocumentCheckQualificationFromMultipleQualifications } from "@src/appV2/redesign/Documents/helpers";
import { useGetAvailableAndAppliedShiftBoosts } from "@src/appV2/redesign/Shift/ShiftBoost/api/useGetAvailableAndAppliedShiftBoosts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { minutesToMilliseconds } from "date-fns";

import { useGetBookabilityStatusForShifts } from "../../OpenShifts/ShiftAction/api/useGetBookabilityStatusForShifts";
import { NON_REJECTED_WORKER_REQUIREMENT_STATUSES } from "../BackgroundChecks/constants";
import { useFilterBackgroundCheckRequirements } from "../BackgroundChecks/useFilterBackgroundCheckRequirements";
import { useHasHourRestrictionConflict } from "../Shift/HourRestrictionConflicts/hooks/useHasHourRestrictionConflict";
import { useGetShiftModal } from "../Shift/Open/useGetShiftModal";
import { useGetShiftSlotsV2 } from "../Shift/Open/useGetShiftsSlotV2";
import { useEvaluatePreShiftOrientationForOpenShift } from "../Shift/PreShiftOrientation/useEvaluatePreShiftOrientationForOpenShift";
import { getRefetchIntervalForAuctionShiftInMilliseconds } from "../Shift/ShiftAuction/utils";
import { useShiftDiscoveryUserFiltersContext } from "./Filters/useUserFiltersContext";
import { useArrivalTimeData } from "./useArrivalTimeData";
import { useFriendsAndColleaguesData } from "./useFriendsAndColleaguesData";
import { useLoadingStateTimings } from "./useLoadingStateTimings";

interface UseGetModalShiftDataProps {
  shiftId?: string;
}

/**
 * Encapsulates data fetching for shift modals.
 *
 * Fetches shift details and bookability status for a shift, needed to open a shift modal.
 * Also provides missing requirements and hours restriction conflict data.

 * TODO: Refactor the useGetModalShiftData hook
 * The hook is doing too many things:
 *   - Fetching shift details
 *   - Fetching bookability status
 *   - Fetching blocking requirements
 *   - Fetching shift boosts
 *   - Fetching shift slots
 *   - Fetching friends/colleagues data
 *   - Fetching route estimates
 *   - Complex conditional loading logic
 *   - Error state aggregation

 * I propose we split this hook with a domain driven approach where this hook is the orchestrator and its child-hooks are domain specific
 * Then, we can remove the cognitive complexity lint warnings.
 * However, I'll leave this refactor as a to-do as its much higher lift.
 */
export function useGetModalShiftData(props: UseGetModalShiftDataProps) {
  const { shiftId } = props;

  const { userId: workerId, hideAsColleagueOnShifts } = useDefinedWorker();
  const { filterOutBackgroundCheckRequirements } = useFilterBackgroundCheckRequirements();

  const { licenses: selectedLicensesFilter = [], availableLicenses = [] } =
    useShiftDiscoveryUserFiltersContext();

  const {
    data: { shift, qualification },
    isLoading: shiftIsLoading,
    isError: shiftIsError,
    offerIsLoading,
  } = useGetShiftModal(shiftId);

  const { workplace } = shift ?? {};

  const { shiftBookabilityById, queryResult: bookabilityStatusQueryResult } =
    useGetBookabilityStatusForShifts(
      {
        shiftIds: [shiftId ?? ""],
        agentId: workerId,
        returnFirstUnmetCriteria: false,
      },
      {
        enabled: isDefined(shiftId),
        refetchInterval: (response) => {
          // We use response data to check if the shift auction is still active.
          // We need to use response data that updates on each query, in order to update the refetch interval accordingly for the next call.
          const shiftAuctionBookabilityStatus = hasUnmetBookabilityCriteria(response?.data[0], [
            BookabilityUnmetCriteria.SHIFT_AUCTION_ACTIVE,
          ]);
          const shiftAuction = shift?.shiftAuction;

          return getRefetchIntervalForAuctionShiftInMilliseconds({
            auctionEndTime: shiftAuction?.attributes.endTime,
            auctionStatusIsOpen: shiftAuctionBookabilityStatus,
            defaultRefetchInterval: minutesToMilliseconds(5),
          });
        },
        refetchIntervalInBackground: true,
        refetchOnMount: "always",
        refetchOnWindowFocus: "always",
        refetchOnReconnect: "always",
      }
    );

  const shiftBoostConfig = useCbhFlag(CbhFeatureFlag.SHIFT_BOOST_CONFIG, {
    defaultValue: { enabled: false },
  });

  const { availableShiftBoost, appliedShiftBoost, shiftBoostsAreLoading } =
    useGetAvailableAndAppliedShiftBoosts(
      { workerId, shiftId },
      { enabled: shiftBoostConfig.enabled }
    );

  const shiftSlots = useGetShiftSlotsV2({
    shiftIds: shiftId ? [shiftId] : [],
  });

  const { friendsMap, recentColleaguesMap, outboundFriendRequestsSet } =
    useFriendsAndColleaguesData({
      hideAsColleagueOnShifts,
    });

  const { isLoading: bookabilityIsLoading, isError: bookabilityIsError } =
    bookabilityStatusQueryResult;

  const bookabilityStatus = isDefined(shiftId) ? shiftBookabilityById[shiftId] : undefined;

  const resolvedShiftQualification = getDocumentCheckQualificationFromMultipleQualifications({
    shiftQualificationRequirement: qualification,
    selectedWorkerQualifications:
      selectedLicensesFilter.length > 0 ? selectedLicensesFilter : availableLicenses,
  });

  const { data: blockingRequirements, isLoading: isLoadingBlockingRequirements } =
    useGetBlockingRequirementsForShift(
      {
        shift,
        qualification: resolvedShiftQualification,
      },
      {
        staleTime: 0, // We want to fetch the latest worker requirements for the shift on every open
        refetchOnMount: "always",
      }
    );

  const { hasHoursRestrictionConflict, isLoading: hasHoursRestrictionConflictIsLoading } =
    useHasHourRestrictionConflict({
      shift: isDefined(shift)
        ? {
            id: shift.id,
            start: shift.attributes.start,
            end: shift.attributes.end,
          }
        : undefined,
      workplace: isDefined(workplace)
        ? {
            id: workplace.id,
            timezone: workplace.attributes.location.timezone,
            preventDoubleShifts: workplace.attributes.preventDoubleShifts,
            maxAllowedWorkHoursPerWeek: workplace.attributes.maxAllowedWorkHoursPerWeek,
            maxAllowedWorkConsecutiveHours: workplace.attributes.maxAllowedWorkConsecutiveHours,
          }
        : undefined,
    });

  const isInstantBook = !hasInstantBookCriteriaUnmet(bookabilityStatus);

  const {
    hasPreShiftOrientation,
    adjustedStart,
    orientationLengthInMinutes,
    isLoading: isPreShiftOrientationLoading,
  } = useEvaluatePreShiftOrientationForOpenShift({
    shiftStartIso: shift?.attributes.start ?? "",
    workplaceId: workplace?.id ?? "",
    preShiftOrientationLengthInMinutes: workplace?.attributes.preShiftOrientationLengthInMinutes,
  });

  const effectiveStartIso =
    hasPreShiftOrientation && isDefined(adjustedStart)
      ? adjustedStart.toISOString()
      : shift?.attributes.start;

  const {
    computedArrivalData,
    arrivalTimePoints,
    isLoadingArrivalTimePoints,
    isRouteEstimateLoadingAndEnabled,
    isLoadingDoubleBookedShift,
  } = useArrivalTimeData({
    workerId,
    shift,
    workplace,
    isInstantBook,
    resolvedShiftQualification,
    effectiveStartIso,
  });

  const isLoading =
    isDefined(shiftId) &&
    (shiftIsLoading ||
      bookabilityIsLoading ||
      offerIsLoading ||
      isLoadingBlockingRequirements ||
      hasHoursRestrictionConflictIsLoading ||
      (shiftBoostConfig.enabled && shiftBoostsAreLoading) ||
      isLoadingArrivalTimePoints ||
      isRouteEstimateLoadingAndEnabled ||
      isLoadingDoubleBookedShift ||
      isPreShiftOrientationLoading);

  // Offer errors (e.g. 403 when the worker can't instant-book) are expected, not load failures.
  const shiftFailedLoading = shiftIsError || bookabilityIsError;
  const errorReasons = [
    shiftIsError ? "shift" : undefined,
    bookabilityIsError ? "bookability" : undefined,
  ].filter(isDefined);

  // Track individual loading state transitions
  const stateTransitionTimings = useLoadingStateTimings({
    shiftIsLoading,
    bookabilityIsLoading,
    offerIsLoading,
    isLoadingBlockingRequirements,
    hasHoursRestrictionConflictIsLoading,
    shiftBoostsAreLoading: shiftBoostConfig.enabled && shiftBoostsAreLoading,
    isLoadingArrivalTimePoints,
    isRouteEstimateLoadingAndEnabled,
    isLoadingDoubleBookedShift,
    totalLoading: isLoading,
  });

  return {
    data: {
      shift,
      availableShiftBoost,
      appliedShiftBoost,
      resolvedShiftQualification,
      bookabilityStatus,
      blockingRequirements:
        // ACT-3210: When run-background-check-on-booking is enabled, we hide other than REJECTED UI for Checkr background checks.
        // For non-rejected statuses, such as MISSING, APPROVED, EXPIRING, IN_REVIEW, EXPIRED, we want to hide the background check
        // details since the worker cannot take an action on it (as it runs automatically on booking).
        isDefined(blockingRequirements)
          ? filterOutBackgroundCheckRequirements({
              workerRequirements: blockingRequirements,
              filter: {
                status: NON_REJECTED_WORKER_REQUIREMENT_STATUSES,
              },
            })
          : blockingRequirements,
      hasHoursRestrictionConflict,
      shiftSlots,
      friendsMap,
      recentColleaguesMap,
      outboundFriendRequestsSet,
      computedArrivalData,
      arrivalTimePoints,
      hasPreShiftOrientation,
      preShiftOrientationAdjustedStart: adjustedStart,
      preShiftOrientationLengthInMinutes: orientationLengthInMinutes,
    },
    isLoading,
    shiftFailedLoading,
    shiftFailedReasons: shiftFailedLoading ? errorReasons : undefined,
    stateTransitionTimings,
  };
}
